import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSiteContent } from "../hooks/useSiteContent";
import { useDemoModeOptional } from "../context/DemoModeContext";
import { X, Sparkles } from "lucide-react";

const STORAGE_KEY = "epp_welcome_popup_seen";

export default function WelcomePopup() {
  const demo = useDemoModeOptional();
  const { popup = {} } = useSiteContent();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (demo?.isDemoSession) return;
    if (!popup.enabled || !(popup.title || popup.message)) return;
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === "1") return;
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    } catch {
      setVisible(true);
    }
  }, [demo?.isDemoSession, popup.enabled, popup.title, popup.message]);

  const close = () => {
    setVisible(false);
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch {}
  };

  if (demo?.isDemoSession || !visible) return null;

  const link = (popup.link || "").trim();
  const linkLabel = (popup.linkLabel || "Voir").trim() || "Voir";

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end justify-center p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="popup-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-[#0f0a24]/65 backdrop-blur-[6px]"
        onClick={close}
        aria-label="Fermer la fenêtre"
      />
      <div className="relative z-10 flex max-h-[min(92dvh,640px)] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl border border-white/10 bg-white shadow-[0_-12px_48px_rgba(25,2,118,0.2)] sm:max-h-[90vh] sm:rounded-3xl sm:border-gray-200/80 sm:shadow-2xl">
        <div className="h-1 w-full shrink-0 bg-gradient-to-r from-[#190276] via-[#3d1db8] to-[#f7040a]" aria-hidden />
        <button
          type="button"
          onClick={close}
          className="absolute right-3 top-3 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-gray-900/5 text-gray-600 ring-1 ring-gray-900/5 transition hover:bg-gray-900/10"
          aria-label="Fermer"
        >
          <X className="h-5 w-5" strokeWidth={2} />
        </button>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {popup.image && (
            <div className="relative aspect-[21/9] w-full overflow-hidden bg-gradient-to-br from-[#f0f2f8] to-[#e8eaf0] sm:aspect-[2/1]">
              <img src={popup.image} alt="" className="h-full w-full object-cover" />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" aria-hidden />
            </div>
          )}
          <div className="px-5 pb-6 pt-5 sm:px-8 sm:pb-8 sm:pt-7">
            {!popup.image && (
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#190276]/10 text-[#190276]">
                <Sparkles className="h-6 w-6" strokeWidth={1.8} />
              </div>
            )}
            {popup.title && (
              <h2
                id="popup-title"
                className="font-heading text-xl font-bold tracking-tight text-gray-900 sm:text-2xl"
              >
                {popup.title}
              </h2>
            )}
            {popup.message && (
              <p className="mt-3 whitespace-pre-line text-[15px] leading-relaxed text-gray-600">
                {popup.message}
              </p>
            )}
            <div className="mt-6 flex flex-col gap-2.5 sm:flex-row sm:flex-wrap">
              {link &&
                (link.startsWith("http") ? (
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-[48px] flex-1 items-center justify-center rounded-xl bg-[#190276] px-5 text-sm font-bold text-white shadow-lg shadow-[#190276]/25 transition hover:bg-[#130160] active:scale-[0.99]"
                  >
                    {linkLabel}
                  </a>
                ) : (
                  <Link
                    to={link.startsWith("/") ? link : `/${link}`}
                    onClick={close}
                    className="inline-flex min-h-[48px] flex-1 items-center justify-center rounded-xl bg-[#190276] px-5 text-sm font-bold text-white shadow-lg shadow-[#190276]/25 transition hover:bg-[#130160] active:scale-[0.99]"
                  >
                    {linkLabel}
                  </Link>
                ))}
              <button
                type="button"
                onClick={close}
                className="inline-flex min-h-[48px] flex-1 items-center justify-center rounded-xl border-2 border-gray-200 bg-white px-5 text-sm font-semibold text-gray-800 transition hover:border-gray-300 hover:bg-gray-50 active:scale-[0.99] sm:flex-initial sm:min-w-[120px]"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
